import {useEffect, useLayoutEffect, useMemo} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Outlet,
  Route,
  RouterProvider,
} from 'react-router-dom';
import {OfflineRouteGuard} from 'src/app/routing/OfflineRouteGuard';
import {PrivateOutlet} from 'src/app/routing/PrivateOutlet';
import {StartupCheckOutlet} from 'src/app/routing/StartupCheckOutlet';
import {useAppDispatch} from 'src/redux/hooks';
import {refreshAccessTokenCache} from 'src/redux/slices/authSlice';
import EmbeddedBrowserPage from 'src/pages/Common/EmbeddedBrowser/EmbeddedBrowser.page';
import IncompatibleBrowserPage from 'src/pages/Common/IncompatibleBrowser/IncompatibleBrowser.page';
import {LoadingPage} from 'src/pages/Common/Loading/Loading.page';
import {OfflineUnavailablePage} from 'src/pages/Common/OfflineUnavailable/OfflineUnavailable.page';
import UnhandledError from 'src/pages/Common/UnhandledError/UnhandledError.page';
import {routeRegistry} from 'src/pages/RouteRegistry';
import type {AppRouteHandle} from 'src/shared/services/backTrail';
import {trackBackTrail} from 'src/shared/services/backTrail';

// Pathless parent route so this boundary wraps every matched route.
const RootErrorBoundary = () => (
  <ErrorBoundary
    FallbackComponent={(e: any) => <UnhandledError error={e.message} />}>
    <Outlet />
  </ErrorBoundary>
);

const buildRouter = () =>
  createBrowserRouter(
    createRoutesFromElements(
      <Route element={<RootErrorBoundary />}>
        <Route
          element={
            <StartupCheckOutlet
              LoadingPage={LoadingPage}
              EmbeddedBrowserPage={EmbeddedBrowserPage}
              IncompatibleBrowserPage={IncompatibleBrowserPage}
              UnhandledErrorPage={UnhandledError}
            />
          }>
          {routeRegistry.getRoutes().map((route, index) => {
            const {
              module,
              operation,
              operationName,
              roles,
              allowGlobalAdminBypass,
              requireSuperAdmin,
              isPrivate = false,
              supportsOffline,
              path,
              element,
              backNavigation,
            } = route;
            const handle: AppRouteHandle = {backNavigation};

            // Public routes are reachable offline (login, embedded browser) —
            // only private routes gate on an offline session.
            if (!isPrivate) {
              return (
                <Route
                  key={index}
                  path={path}
                  element={element}
                  handle={handle}
                />
              );
            }

            const guardedElement = (
              <OfflineRouteGuard
                supportsOffline={supportsOffline}
                OfflineUnavailablePage={OfflineUnavailablePage}>
                {element}
              </OfflineRouteGuard>
            );

            // For private routes, use the PrivateOutlet as the element and render the actual element as a child route
            return (
              <Route
                key={index}
                path={path}
                handle={handle}
                element={
                  <PrivateOutlet
                    module={module}
                    operation={operation}
                    operationName={operationName}
                    roles={roles}
                    allowGlobalAdminBypass={allowGlobalAdminBypass}
                    requireSuperAdmin={requireSuperAdmin}
                    LoadingPage={LoadingPage}
                  />
                }>
                <Route index element={guardedElement} />
              </Route>
            );
          })}
        </Route>
      </Route>
    )
  );

const Routing = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    void dispatch(refreshAccessTokenCache());
  }, []);

  // Built once per mount. Routes are registered as side effects of the
  // module imports in `src/pages/routes` (imported before App renders), so
  // the registry is fully populated by the time this component first runs.
  const router = useMemo(buildRouter, []);

  useLayoutEffect(() => trackBackTrail(router), [router]);

  return <RouterProvider router={router} />;
};

export default Routing;
